import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import { api } from "../../api/client";
import { downloadTaxReportPdf } from "../../api/taxReportApi";

const TAX_REPORT_BASE = "/api/tax-report";

const toCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const getLocalizedText = (value, fallback = "N/A") => {
  if (typeof value === "string") return value || fallback;
  if (value && typeof value === "object") {
    return value.en || value.ar || Object.values(value)[0] || fallback;
  }
  return fallback;
};

const normalizeFilterOption = (item = {}, type = "store") => {
  const isStore = type === "store";
  const value =
    item?._id ||
    item?.id ||
    (isStore ? item?.storeId || item?.vendorStoreId : item?.productId) ||
    "";
  const label = isStore
    ? getLocalizedText(item?.storeName || item?.name || item?.title, "Store")
    : getLocalizedText(
        item?.productName || item?.name || item?.title || item?.sku,
        "Product",
      );
  return { value: String(value), label };
};

const formatDateForApi = (dateObj) => {
  if (!dateObj) return "";
  const parsed = dayjs(dateObj);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const buildReportParams = (filters = {}, extra = {}) => {
  const params = {
    sortBy: filters?.sortBy || "date",
    sortOrder: filters?.sortOrder || "desc",
    ...extra,
  };

  const date = formatDateForApi(filters?.date);
  const from = formatDateForApi(filters?.from);
  const to = formatDateForApi(filters?.to);

  if (date) {
    params.date = date;
  } else {
    if (from) params.from = from;
    if (to) params.to = to;
  }

  if (filters?.storeId) params.storeId = filters.storeId;
  if (filters?.productId) params.productId = filters.productId;
  if (filters?.paymentMethod) params.paymentMethod = filters.paymentMethod;

  const search = String(filters?.search || "").trim();
  if (search) params.search = search;

  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
};

const mapReportRow = (row = {}, index = 0) => {
  const rawDate = row?.date || row?.createdAt || "";
  const formattedDate = dayjs(rawDate).isValid()
    ? dayjs(rawDate).format("DD/MM/YYYY")
    : "-";

  return {
    id: row?._id || `${row?.reference || "row"}-${index}`,
    reference: row?.reference || row?.orderId || "-",
    customer: getLocalizedText(row?.customer, "Walk-in"),
    date: formattedDate,
    store: getLocalizedText(row?.store, "-"),
    amount: Number(row?.amount || 0),
    paymentMethod: row?.paymentMethod || row?.type || "-",
    discount: Number(row?.discount || 0),
    taxAmount: Number(row?.taxAmount || 0),
    paidAmount: Number(row?.paidAmount || 0),
    unpaidAmount: Number(row?.unpaidAmount || 0),
  };
};

const SaleReport = () => {
  const t = (text) => (window.translate ? window.translate(text) : text);

  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState("");

  const [tableRows, setTableRows] = useState([]);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalPaid: 0,
    totalUnpaid: 0,
    totalOverdue: 0,
    totalTaxAmount: 0,
    totalDiscountAmount: 0,
    totalTransactions: 0,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const [storeOptions, setStoreOptions] = useState([
    { value: "", label: t("All Stores") },
  ]);
  const [productOptions, setProductOptions] = useState([
    { value: "", label: t("All Products") },
  ]);

  const [filters, setFilters] = useState({
    date: null,
    from: null,
    to: null,
    storeId: localStorage.getItem("storeId") || "",
    productId: "",
    paymentMethod: "",
    search: "",
    sortBy: "date",
    sortOrder: "desc",
  });
  const [submittedFilters, setSubmittedFilters] = useState({
    date: null,
    from: null,
    to: null,
    storeId: localStorage.getItem("storeId") || "",
    productId: "",
    paymentMethod: "",
    search: "",
    sortBy: "date",
    sortOrder: "desc",
  });

  const paymentMethodOptions = useMemo(
    () => [
      { value: "", label: t("All Payment Methods") },
      { value: "cash", label: t("Cash") },
      { value: "card", label: t("Card") },
      { value: "scan", label: t("Scan") },
      { value: "split", label: t("Split Payment") },
    ],
    [],
  );

  const loadFilterOptions = async () => {
    try {
      setFiltersLoading(true);
      const response = await api.get(`${TAX_REPORT_BASE}/filters`);
      const payload = response?.data?.data || response?.data || {};

      const stores = Array.isArray(payload?.stores) ? payload.stores : [];
      const products = Array.isArray(payload?.products) ? payload.products : [];

      setStoreOptions([
        { value: "", label: t("All Stores") },
        ...stores.map((store) => normalizeFilterOption(store, "store")),
      ]);
      setProductOptions([
        { value: "", label: t("All Products") },
        ...products.map((product) => normalizeFilterOption(product, "product")),
      ]);
    } catch (error) {
      message.error(error?.message || t("Failed to load tax report filters"));
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchTaxReport = async (
    activeFilters = submittedFilters,
    page = pagination.page,
    limit = pagination.limit,
  ) => {
    try {
      setLoading(true);

      const listParams = buildReportParams(activeFilters, {
        page,
        limit,
      });
      const summaryParams = buildReportParams(activeFilters);

      const [listResponse, summaryResponse] = await Promise.all([
        api.get(`${TAX_REPORT_BASE}/list`, { params: listParams }),
        api.get(`${TAX_REPORT_BASE}/summary`, { params: summaryParams }),
      ]);

      const listPayload = listResponse?.data || {};
      const summaryPayload =
        summaryResponse?.data?.data ||
        summaryResponse?.data ||
        listPayload?.summary ||
        {};

      const rawRows = Array.isArray(listPayload?.data) ? listPayload.data : [];
      setTableRows(rawRows.map(mapReportRow));

      setSummary({
        totalAmount: Number(summaryPayload?.totalAmount || 0),
        totalPaid: Number(summaryPayload?.totalPaid || 0),
        totalUnpaid: Number(summaryPayload?.totalUnpaid || 0),
        totalOverdue: Number(summaryPayload?.totalOverdue || 0),
        totalTaxAmount: Number(summaryPayload?.totalTaxAmount || 0),
        totalDiscountAmount: Number(summaryPayload?.totalDiscountAmount || 0),
        totalTransactions: Number(summaryPayload?.totalTransactions || 0),
      });

      const listPagination = listPayload?.pagination || {};
      setPagination({
        total: Number(listPagination?.total || rawRows.length || 0),
        page: Number(listPagination?.page || page || 1),
        limit: Number(listPagination?.limit || limit || 10),
        totalPages: Number(listPagination?.totalPages || 1),
      });
    } catch (error) {
      setTableRows([]);
      setSummary({
        totalAmount: 0,
        totalPaid: 0,
        totalUnpaid: 0,
        totalOverdue: 0,
        totalTaxAmount: 0,
        totalDiscountAmount: 0,
        totalTransactions: 0,
      });
      setPagination({
        total: 0,
        page: 1,
        limit,
        totalPages: 1,
      });
      message.error(error?.message || t("Failed to fetch tax report"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilterOptions();
    fetchTaxReport(submittedFilters, 1, pagination.limit);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateReport = async (event) => {
    event.preventDefault();
    setSubmittedFilters(filters);
    await fetchTaxReport(filters, 1, pagination.limit);
  };

  const handleRefresh = async () => {
    await fetchTaxReport(submittedFilters, pagination.page, pagination.limit);
  };

  const handlePageChange = async (nextPage) => {
    if (nextPage < 1 || nextPage > Number(pagination.totalPages || 1)) return;
    await fetchTaxReport(submittedFilters, nextPage, pagination.limit);
  };

  const handleLimitChange = async (event) => {
    const nextLimit = Number(event.target.value || 10);
    if (!Number.isFinite(nextLimit) || nextLimit <= 0) return;
    await fetchTaxReport(submittedFilters, 1, nextLimit);
  };

  const getExportFileStamp = () => dayjs().format("YYYYMMDD-HHmmss");

  const getExportRows = () =>
    tableRows.map((row) => ({
      [t("Reference")]: row.reference,
      [t("Customer")]: row.customer,
      [t("Date")]: row.date,
      [t("Store")]: row.store,
      [t("Amount")]: Number(row.amount || 0),
      [t("Payment Method")]: row.paymentMethod,
      [t("Discount")]: Number(row.discount || 0),
      [t("Tax Amount")]: Number(row.taxAmount || 0),
      [t("Paid Amount")]: Number(row.paidAmount || 0),
      [t("Unpaid Amount")]: Number(row.unpaidAmount || 0),
    }));

  const handleExportExcel = () => {
    try {
      setExportLoading("excel");
      const exportRows = getExportRows();

      const workbook = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.aoa_to_sheet([
        [t("Metric"), t("Value")],
        [t("Total Amount"), Number(summary.totalAmount || 0)],
        [t("Total Paid"), Number(summary.totalPaid || 0)],
        [t("Total Unpaid"), Number(summary.totalUnpaid || 0)],
        [t("Overdue"), Number(summary.totalOverdue || 0)],
        [t("Total Transactions"), Number(summary.totalTransactions || 0)],
      ]);
      XLSX.utils.book_append_sheet(workbook, summarySheet, t("Summary"));

      const tableSheet = XLSX.utils.json_to_sheet(exportRows);
      XLSX.utils.book_append_sheet(workbook, tableSheet, t("Sales Tax Report"));

      XLSX.writeFile(workbook, `sales-tax-report-${getExportFileStamp()}.xlsx`);
      message.success(t("Sales tax report excel downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to download sales tax report excel"));
    } finally {
      setExportLoading("");
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportLoading("pdf");
      await downloadTaxReportPdf(buildReportParams(submittedFilters));
      message.success(t("Sales tax report pdf downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to download sales tax report pdf"));
    } finally {
      setExportLoading("");
    }
  };

  const columns = [
    {
      title: t("Reference"),
      dataIndex: "reference",
      render: (text) => (
        <Link to="#" className="text-orange">
          {text}
        </Link>
      ),
      sorter: (a, b) => String(a.reference || "").localeCompare(String(b.reference || "")),
    },
    {
      title: t("Customer"),
      dataIndex: "customer",
      sorter: (a, b) => String(a.customer || "").localeCompare(String(b.customer || "")),
    },
    {
      title: t("Date"),
      dataIndex: "date",
      sorter: (a, b) => String(a.date || "").localeCompare(String(b.date || "")),
    },
    {
      title: t("Store"),
      dataIndex: "store",
      sorter: (a, b) => String(a.store || "").localeCompare(String(b.store || "")),
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
    },
    {
      title: t("Payment Method"),
      dataIndex: "paymentMethod",
      sorter: (a, b) =>
        String(a.paymentMethod || "").localeCompare(String(b.paymentMethod || "")),
    },
    {
      title: t("Discount"),
      dataIndex: "discount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.discount || 0) - Number(b.discount || 0),
    },
    {
      title: t("Tax Amount"),
      dataIndex: "taxAmount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.taxAmount || 0) - Number(b.taxAmount || 0),
    },
    {
      title: t("Paid Amount"),
      dataIndex: "paidAmount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.paidAmount || 0) - Number(b.paidAmount || 0),
    },
    {
      title: t("Unpaid Amount"),
      dataIndex: "unpaidAmount",
      render: (value) => toCurrency(value),
      sorter: (a, b) => Number(a.unpaidAmount || 0) - Number(b.unpaidAmount || 0),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{t("Sales Tax Report")}</h4>
              <h6>{t("Manage your sales tax report")}</h6>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn px-3"
              onClick={handleExportExcel}
              disabled={Boolean(exportLoading)}
              style={{
                border: "1px solid #0d6efd",
                color: "#0d6efd",
                background: "#fff",
                minWidth: "130px",
                minHeight: "42px",
              }}
            >
              {exportLoading === "excel" ? t("Please wait...") : t("Export Excel")}
            </button>
            <button
              type="button"
              className="btn px-3"
              onClick={handleExportPdf}
              disabled={Boolean(exportLoading)}
              style={{
                border: "1px solid #198754",
                color: "#198754",
                background: "#fff",
                minWidth: "130px",
                minHeight: "42px",
              }}
            >
              {exportLoading === "pdf" ? t("Please wait...") : t("Export PDF")}
            </button>
          <ul className="table-top-head">
            <li>
              <Link to="#" onClick={handleRefresh}>
                <RefreshIcon />
              </Link>
            </li>
            <CollapesIcon />
          </ul>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-success sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-success text-white">
                  <i className="ti ti-align-box-bottom-left-filled fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Total Amount")}</p>
                  <h3>{toCurrency(summary.totalAmount)}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-info sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-info text-white">
                  <i className="ti ti-wallet fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Total Paid")}</p>
                  <h3>{toCurrency(summary.totalPaid)}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-orange sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-orange text-white">
                  <i className="ti ti-moneybag fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Total Unpaid")}</p>
                  <h3>{toCurrency(summary.totalUnpaid)}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-danger sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-danger text-white">
                  <i className="ti ti-alert-circle-filled fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{t("Overdue")}</p>
                  <h3>{toCurrency(summary.totalOverdue)}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0">
          <div className="card-body pb-1">
            <form onSubmit={handleGenerateReport}>
              <div className="row align-items-end">
                <div className="col-lg-10">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={t("dd/mm/yyyy")}
                            value={filters.date ? dayjs(filters.date) : null}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                date: value ? value.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("From Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={t("dd/mm/yyyy")}
                            value={filters.from ? dayjs(filters.from) : null}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                from: value ? value.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("To Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={t("dd/mm/yyyy")}
                            value={filters.to ? dayjs(filters.to) : null}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                to: value ? value.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Store")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={storeOptions}
                          isLoading={filtersLoading}
                          placeholder={t("Choose")}
                          value={
                            storeOptions.find(
                              (option) => option.value === filters.storeId,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              storeId: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Products")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={productOptions}
                          isLoading={filtersLoading}
                          placeholder={t("Choose")}
                          value={
                            productOptions.find(
                              (option) => option.value === filters.productId,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              productId: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{t("Payment Method")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={paymentMethodOptions}
                          placeholder={t("Choose")}
                          value={
                            paymentMethodOptions.find(
                              (option) => option.value === filters.paymentMethod,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              paymentMethod: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">{t("Search")}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("Search by order reference")}
                          value={filters.search}
                          onChange={(event) =>
                            setFilters((prev) => ({
                              ...prev,
                              search: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-2">
                  <div className="mb-3">
                    <button className="btn btn-primary w-100" type="submit">
                      {t("Generate Report")}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="card table-list-card no-search">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div>
              <h4>{t("Sales Tax Report")}</h4>
              <small className="text-muted">
                {t("Total")}: {pagination.total} | {t("Page")} {pagination.page}/
                {pagination.totalPages}
              </small>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link
                  to="#"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={t("Print")}
                >
                  <i className="ti ti-printer" />
                </Link>
              </li>
            </ul>
          </div>
          <div className="card-body">
            <div className="table-responsive custome-search">
              <Table columns={columns} dataSource={tableRows} loading={loading} />
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0">{t("Rows")}</label>
                <select
                  className="form-select form-select-sm"
                  value={pagination.limit}
                  onChange={handleLimitChange}
                  style={{ width: 90 }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={loading || pagination.page <= 1}
                >
                  {t("Previous")}
                </button>
                <span className="fw-medium">
                  {t("Page")} {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={loading || pagination.page >= pagination.totalPages}
                >
                  {t("Next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommonFooter />
    </div>
  );
};

export default SaleReport;
